/**
 * HTTP Client
 * Core HTTP client for making API requests
 */

import { buildFullURL } from "./url-builder";
import { parseResponse } from "./response-parser";
import { FetchError } from "./errors";
import { DEFAULT_TIMEOUT, HTTP_METHODS } from "./constants";
import type { FetchOptions, ApiConfig } from "./types";
import { useAuthStore } from "@/lib/auth/store";

// Shared across concurrent 401s so we only refresh once per wave.
let refreshPromise: Promise<boolean> | null = null;

function refreshAccessToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = useAuthStore
      .getState()
      .refresh()
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

const API_CONFIG: ApiConfig = {
  backend: {
    baseUrl:
      process.env.API_URL ||
      process.env.NEXT_PUBLIC_API_URL ||
      "http://localhost:8000",
    apiPrefix: "/api/v1",
  },
  nextjs: {
    proxyPrefix: "/api/proxy",
    serverUrl: process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:3000",
  },
};

/**
 * HTTP GET request
 */
export const httpGet = <T>(url: string, options?: FetchOptions): Promise<T> =>
  fetchClient<T>(url, { ...options, method: HTTP_METHODS.GET });

/**
 * HTTP DELETE request
 */
export const httpDelete = <T>(
  url: string,
  options?: FetchOptions,
): Promise<T> =>
  fetchClient<T>(url, { ...options, method: HTTP_METHODS.DELETE });

/**
 * Factory function to create HTTP methods that send a body (POST, PUT, PATCH)
 */
function createBodyMethod(method: string) {
  return <TResponse = any, TBody = any>(
    url: string,
    body: TBody,
    options?: FetchOptions,
  ): Promise<TResponse> =>
    fetchClient<TResponse>(url, {
      ...options,
      method,
      body: JSON.stringify(body),
    });
}

/**
 * HTTP POST request
 */
export const httpPost = createBodyMethod(HTTP_METHODS.POST);

/**
 * HTTP PUT request
 */
export const httpPut = createBodyMethod(HTTP_METHODS.PUT);

/**
 * HTTP PATCH request
 */
export const httpPatch = createBodyMethod(HTTP_METHODS.PATCH);

export const httpPostFormData = <T>(
  url: string,
  formData: FormData,
  options?: Omit<FetchOptions, "body">,
): Promise<T> => {
  const { headers, ...rest } = options || {};
  const cleanHeaders = new Headers(headers);
  cleanHeaders.delete("Content-Type");
  return fetchClient<T>(url, {
    ...rest,
    method: HTTP_METHODS.POST,
    body: formData,
    headers: cleanHeaders,
  });
};

/**
 * Core fetch client
 * Handles HTTP requests with timeout, error handling, and response parsing
 */
async function fetchClient<T>(
  url: string,
  options?: FetchOptions,
  retried = false,
): Promise<T> {
  const { params, timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options || {};

  const isClient = typeof window !== "undefined";
  const fullUrlString = buildFullURL(url, isClient, API_CONFIG);
  const fullUrl = new URL(fullUrlString);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        fullUrl.searchParams.set(key, String(value));
      }
    });
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  const headers = new Headers(fetchOptions.headers);
  if (!(fetchOptions.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (isClient && !headers.has("Authorization")) {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  try {
    const response = await fetch(fullUrl.toString(), {
      ...fetchOptions,
      signal: controller.signal,
      credentials: "include",
      headers,
    });

    if (!response.ok) {
      // Transparent token refresh + retry for client-side 401s.
      // Server-side requests carry an explicit Authorization header — the
      // SSR path can't refresh, so skip there.
      if (response.status === 401 && isClient && !retried) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          clearTimeout(timeoutId);
          const newToken = useAuthStore.getState().accessToken;
          const retryOptions: FetchOptions = {
            ...options,
            headers: {
              ...(options?.headers ?? {}),
              ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
            },
          };
          return fetchClient<T>(url, retryOptions, true);
        }
      }

      let errorMessage = `HTTP error: ${response.statusText}`;
      let errorMeta: Record<string, unknown> | undefined;
      try {
        const errorBody = await response.json();
        if (errorBody.detail) {
          if (typeof errorBody.detail === "object" && errorBody.detail !== null) {
            // Structured error detail (e.g. { message, quoteStatus })
            errorMessage = errorBody.detail.message || JSON.stringify(errorBody.detail);
            errorMeta = errorBody.detail;
          } else {
            errorMessage = errorBody.detail;
          }
        }
      } catch {
        // Response body is not JSON, use default message
      }
      const fetchError = new FetchError(
        errorMessage,
        response.status,
        response.statusText,
        fullUrl.toString(),
        fetchOptions.method,
      );
      if (errorMeta) {
        fetchError.meta = errorMeta;
      }
      throw fetchError;
    }

    return parseResponse<T>(response);
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new FetchError(
        "Request timeout",
        408,
        "Request Timeout",
        fullUrl.toString(),
        fetchOptions.method,
      );
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}
